/**
 * ============================================================================
 * THEME PROVIDER
 * ============================================================================
 * 
 * Provides theme context to the entire app
 */

import React, { createContext, useContext, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import { useUIStore } from '../store';

const ThemeContext = createContext(null);

/**
 * Theme Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const ThemeProvider = ({ children }) => {
  const themeHook = useTheme();
  const { effectiveTheme, setEffectiveTheme } = useUIStore();

  // Sync hook with store
  useEffect(() => {
    if (themeHook.effectiveTheme !== effectiveTheme) {
      setEffectiveTheme(themeHook.effectiveTheme);
    }
  }, [themeHook.effectiveTheme, effectiveTheme, setEffectiveTheme]);

  // Apply theme to document on mount and changes
  useEffect(() => {
    const root = document.documentElement;
    const theme = themeHook.effectiveTheme;
    
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.setAttribute('data-theme', theme);
  }, [themeHook.effectiveTheme]);

  const value = {
    ...themeHook,
    // Ensure store and hook are in sync
    theme: themeHook.theme,
    effectiveTheme: themeHook.effectiveTheme,
    setTheme: themeHook.setTheme,
    toggleTheme: themeHook.toggleTheme,
    isDark: themeHook.isDark,
    isLight: themeHook.isLight,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Use theme context
 * @returns {Object} Theme context value
 */
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Fallback to hook if context not available
    return useTheme();
  }
  return context;
};

export default ThemeProvider;
